import { clamp, COORDINATE_SPACE } from "@perceptron/utils/math";
import type { Point } from "@perceptron/core/types";

import type { VideoLayout } from "./videoLayout";

export interface BoxCoordinates {
  top_left: { x: number; y: number };
  bottom_right: { x: number; y: number };
}

export interface DisplayBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

export const coordinatesToDisplayBounds = (
  coords: BoxCoordinates,
  videoWidth: number,
  videoHeight: number,
  offsetX: number,
  offsetY: number,
): DisplayBounds => {
  if (![coords.top_left.x, coords.top_left.y, coords.bottom_right.x, coords.bottom_right.y].every(Number.isFinite)) {
    return { x: offsetX, y: offsetY, width: 0, height: 0 };
  }
  // Intersect with the video frame before applying letterbox offsets. Raw
  // annotations and previously retained predictions must obey the same bounds.
  const normalizedTopLeftX = clamp(coords.top_left.x / COORDINATE_SPACE, 0, 1);
  const normalizedTopLeftY = clamp(coords.top_left.y / COORDINATE_SPACE, 0, 1);
  const normalizedBottomRightX = clamp(coords.bottom_right.x / COORDINATE_SPACE, 0, 1);
  const normalizedBottomRightY = clamp(coords.bottom_right.y / COORDINATE_SPACE, 0, 1);

  const x = offsetX + normalizedTopLeftX * videoWidth;
  const y = offsetY + normalizedTopLeftY * videoHeight;
  const width = Math.max(0, (normalizedBottomRightX - normalizedTopLeftX) * videoWidth);
  const height = Math.max(0, (normalizedBottomRightY - normalizedTopLeftY) * videoHeight);

  return { x, y, width, height };
};

export function displayToNormalized(pixelX: number, pixelY: number, layout: VideoLayout): { x: number; y: number } {
  return {
    x: clamp(((pixelX - layout.offsetX) / layout.width) * COORDINATE_SPACE, 0, COORDINATE_SPACE),
    y: clamp(((pixelY - layout.offsetY) / layout.height) * COORDINATE_SPACE, 0, COORDINATE_SPACE),
  };
}

export const pointToDisplayPosition = (
  point: Point,
  videoWidth: number,
  videoHeight: number,
  offsetX: number,
  offsetY: number,
): { x: number; y: number } => ({
  x: offsetX + (point.x / COORDINATE_SPACE) * videoWidth,
  y: offsetY + (point.y / COORDINATE_SPACE) * videoHeight,
});

export const polygonToDisplayPositions = (
  vertices: readonly Point[],
  videoWidth: number,
  videoHeight: number,
  offsetX: number,
  offsetY: number,
): { x: number; y: number }[] =>
  vertices.map((v) => pointToDisplayPosition(v, videoWidth, videoHeight, offsetX, offsetY));
